import SmartSearchIcon from './SmartSearchIcon';
import { smartSceneThemeStyle } from './themeStyle';
import React, { useContext, useEffect, useState } from 'react';
import request from '../../utils/request';
import galleryApi from '../../actions/gallery';
import { ThemeContext } from '../../store/ThemeProvider';
import SearchResult from '../ComponentOcr/SearchResult';
import './index.scss';

/**
 * 按 release 接口每条关系记录的外层字段排序，不使用 detail 中的图片字段。
 *
 * 排序规则（后台与相册前端必须保持一致）：
 * 1. index_number 转为数值后 > 0 的图片优先，按 index_number 降序；
 *    置顶值相同时保留接口原顺序，不再比较 score。
 * 2. 其余图片（index_number 为 0、负数、缺失或无法转为数值等）
 *    按有效 score 升序；0 是有效分数，数字字符串也参与数值比较。
 * 3. score 为 null、undefined、空字符串、纯空白、NaN、正负 Infinity
 *    或其他无法转为有限数值的值时，视为无效分数，排在非置顶图片末尾。
 *    此处是前端兜底约定：null 不作为 0；置顶图片仍只遵循规则 1。
 * 4. 非置顶图片的有效分数相同，或分数都无效时，保留接口原顺序。
 *
 * 返回新数组，不修改输入数组、图片对象及其字段；不按拍摄时间补充排序。
 *
 * @param {Array<Object>} images release 的 images_list 关系记录数组。
 * @returns {Array<Object>} 按上述规则排序后的关系记录数组。
 * @see jfoto_management_static/src/components/FileViewerV2/page/list/pane/smart_scene_api.js
 */
export const sortReleasedImages = (images: any[]) => images
  .map((image, order) => ({ image, order }))
  .sort((a, b) => {
    const aIndex = Number(a.image.index_number) || 0;
    const bIndex = Number(b.image.index_number) || 0;
    if (aIndex > 0 || bIndex > 0) {
      if (aIndex > 0 && bIndex > 0) return bIndex - aIndex || a.order - b.order;
      return aIndex > 0 ? -1 : 1;
    }
    const score = (image: any) => {
      const value = image.score;
      const numeric = Number(value);
      return value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(numeric)
        ? numeric : Infinity;
    };
    const aScore = score(a.image), bScore = score(b.image);
    return (aScore === bScore ? 0 : aScore - bScore) || a.order - b.order;
  })
  .map(item => item.image);

interface Props {
  title?: string;
  description?: string;
  smart_scene_items?: Array<{ id: number; visible?: boolean }>;
  image_sort?: string;
  smart?: boolean;
  enabled?: boolean;
  show_gallery_jumper?: boolean;
}
export default function AllForSmartScene(props: Props) {
  const theme = useContext(ThemeContext);
  const [scenes, setScenes] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const orderId = window.gallery && window.gallery.orderid;
  useEffect(() => {
    setScenes([]); setSelectedId(null);
    if (props.enabled === false || !orderId) return;
    let mounted = true; setFailed(false);
    Promise.all([
      request({ url: `/api/v2/smartscene/${orderId}/list`, method: 'GET', data: { is_show: 1 } }),
      request({ url: `/api/v2/smartscene/${orderId}/release`, method: 'GET' })
    ]).then(async ([sceneResponse, releaseResponse]: any[]) => {
      if (!mounted) return;
      if (!sceneResponse || Number(sceneResponse.code) !== 200 || !releaseResponse || Number(releaseResponse.code) !== 200) throw new Error('Theme load failed');
      const groups = releaseResponse.data || [];
      const albumIds = Array.from(new Set(groups.reduce((ids: string[], group: any) => ids.concat((group.images_list || []).map((item: any) => String(item.detail && (item.detail.galleryid || item.detail.gallery_id) || '')).filter(Boolean)), []))) as string[];
      const albumResponse: any = albumIds.length ? await galleryApi.batch(albumIds) : { data: [] };
      if (!mounted) return;
      if (!albumResponse || !Array.isArray(albumResponse.data)) throw new Error('Album load failed');
      const albums = albumResponse.data;
      setScenes((sceneResponse.data || []).filter((scene: any) => Number(scene.is_show) !== 0).map((scene: any) => {
        const group = groups.find((item: any) => Number(item.smart_scene_id) === Number(scene.id));
        const seen: any = {};
        const images = sortReleasedImages((group && group.images_list) || []).map((item: any) => item.detail).filter((image: any) => {
          if (!image || !image.id || seen[image.id]) return false;
          const publicPhoto = !image.trash || image.trash === '0' || image.trash === '1970-01-01 08:00:00';
          const album = albums.find((item: any) => Number(item.id) === Number(image.galleryid || image.gallery_id));
          if (!publicPhoto || !album || (Number(album.check) === 1 && Number(image.check) !== 1)) return false;
          seen[image.id] = true; return true;
        });
        return { ...scene, images };
      }));
    }).catch(() => { if (mounted) setFailed(true); });
    return () => { mounted = false; };
  }, [orderId, props.enabled, retry]);
  const byId: any = {}; scenes.forEach(scene => { byId[scene.id] = scene; });
  const seen: any = {};
  const ordered = (props.smart_scene_items || []).concat(scenes.slice().sort((a, b) => (Date.parse(String(b.created_at || '').replace(/-/g, '/')) || 0) - (Date.parse(String(a.created_at || '').replace(/-/g, '/')) || 0) || Number(b.id) - Number(a.id)).map(scene => ({ id: scene.id, visible: true }))).filter(item => {
    if (!byId[item.id] || seen[item.id]) return false; seen[item.id] = true; return item.visible !== false && byId[item.id].images.length > 0;
  }).map(item => byId[item.id]);
  const selected = ordered.find(scene => Number(scene.id) === selectedId);
  const selectedImages = selected ? selected.images : [];
  if (props.enabled === false) return null;
  if (failed) return <div className={`smart-scene-public smart-scene-public-error${props.smart ? ' smart-scene-public-smart' : ' smart-scene-public-standalone'}`} style={smartSceneThemeStyle(theme)}><button onClick={() => setRetry(retry + 1)}>主题加载失败，点击重试</button></div>;
  if (!ordered.length) return null;
  return <div className={`smart-scene-public${props.smart ? ' smart-scene-public-smart' : ' smart-scene-public-standalone'}`} style={smartSceneThemeStyle(theme)}>
    {props.title ? <h3><SmartSearchIcon />{props.title}</h3> : null}
    {props.description ? <p>{props.description}</p> : null}
    {props.smart ? <strong className="smart-scene-public-label">精选主题</strong> : null}
    <div className="smart-scene-public-grid">{ordered.map(scene => <button key={scene.id} onClick={() => setSelectedId(Number(scene.id))}><strong>{scene.name}</strong><small>{scene.images.length}张</small><svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>)}</div>
    {selected ? <SearchResult key={selected.id} close={() => setSelectedId(null)} hideTopicHeader topicTitle={selected.name} topicNavigation={ordered.map(scene => ({ id: scene.id, name: scene.name, active: scene.id === selected.id, onSelect: () => setSelectedId(Number(scene.id)) }))} list={selectedImages} showGalleryJumper={props.show_gallery_jumper !== false} recognizedCountText="已为你精选 {count} 张" /> : null}
  </div>;
}
